'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import { List, OrderedMap } from 'immutable';
import Raven from 'raven-js';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { fetchPixels, installAndCreatePixels } from 'AdsUI/actions/AudiencesActions';
import NetworkSelectPixelModal from 'ads-account-connection/components/audiences/NetworkSelectPixelModal';
import { connect } from 'react-redux';
import { getPixelsForInstallation } from 'AdsUI/selectors/PixelInstallationSelectors';
import { postTimedNotification } from 'AdsUI/actions/NotificationActions';
import { MARKETING_STARTER_UPGRADE_DATA } from 'AdsUI/lib/UpgradeData';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import { getAccessibleAdAccounts } from 'AdsUI/selectors/AccountsSelectors';
import { hasAccessToRoiReporting, getFacebookCapiFormEventsUpdates } from 'AdsUI/selectors/AuthSelectors';
import { saveFBEUserToken } from 'AdsUI/data/AccountConnectionData';

const InstallPixelModalContainer = ({
  adAccounts,
  pixelsRequestStatus,
  pixelCreatesRequestStatus,
  pixelInstallsRequestStatus,
  pixelsByNetwork,
  preselectedAdAccount,
  dispatch,
  onClose,
  eventCount,
  eventLimit,
  isEventCreation = false,
  onAuth,
  isProOrEnterprise,
  hasFacebookCapiFormEventsUpdates
}) => {
  const relevantAdAccounts = useMemo(() => preselectedAdAccount ? List([preselectedAdAccount]) : adAccounts, [preselectedAdAccount, adAccounts]);
  const relevantPixelsByNetwork = useMemo(() => preselectedAdAccount ? OrderedMap([[preselectedAdAccount.network, pixelsByNetwork.get(preselectedAdAccount.network).filter(pixel => pixel.accountId === preselectedAdAccount.accountId)]]) : pixelsByNetwork, [preselectedAdAccount, pixelsByNetwork]);
  const onInstallPixelFailed = useCallback(() => {
    dispatch(postTimedNotification({
      id: 'install-pixel-failure',
      type: 'error',
      message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "audiences.wizard.banners.pixelError"
      })
    }));
  }, [dispatch]);
  const onInstallPixels = useCallback((selectedPixels, fbeAuthData) => {
    return dispatch(installAndCreatePixels(selectedPixels, onInstallPixelFailed)).then(() => {
      // Save FBE authorization data if it's supplied
      // This should be completed before refetching the pixels
      if (fbeAuthData) {
        return saveFBEUserToken(fbeAuthData.adAccountId, fbeAuthData.externalBusinessId, fbeAuthData.userToken).catch(err => {
          Raven.captureMessage('Failed to save FBE user token', {
            extra: {
              error: err
            }
          });
        });
      }

      return null;
    }).then(onClose).then(() => {
      dispatch(postTimedNotification({
        id: 'install-pixel-success',
        type: 'success',
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "audiences.wizard.banners.pixelCreate",
          options: {
            count: selectedPixels.size
          }
        })
      }));
    }).then(() => {
      dispatch(fetchPixels());
    });
  }, [dispatch, onClose, onInstallPixelFailed]);
  const onRefreshPixels = useCallback(() => {
    dispatch(fetchPixels());
  }, [dispatch]);
  useEffect(() => {
    if (!pixelsRequestStatus.fetched && !pixelsRequestStatus.fetching) {
      dispatch(fetchPixels());
    }
  }, [pixelsRequestStatus, dispatch]);

  if (!pixelsRequestStatus.fetchedSuccessfully()) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      layout: "centered"
    });
  }

  return /*#__PURE__*/_jsx(NetworkSelectPixelModal, {
    adAccounts: relevantAdAccounts,
    installPixels: onInstallPixels,
    loading: pixelCreatesRequestStatus.syncing || pixelInstallsRequestStatus.syncing,
    onClose: onClose,
    onRefreshPixels: onRefreshPixels,
    disableInstalled: true,
    pixelsByNetwork: relevantPixelsByNetwork,
    starterUpgradeData: MARKETING_STARTER_UPGRADE_DATA,
    trackEvent: usageTracker.track,
    eventCount: eventCount,
    eventLimit: eventLimit,
    isEventCreation: isEventCreation,
    onAuth: onAuth,
    isProOrEnterprise: isProOrEnterprise,
    hasFacebookCapiFormEventsUpdates: hasFacebookCapiFormEventsUpdates
  });
};

InstallPixelModalContainer.propTypes = {
  onAuth: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  eventCount: PropTypes.number,
  eventLimit: PropTypes.number,
  dispatch: PropTypes.func,
  pixelsRequestStatus: PropTypes.shape({
    fetched: PropTypes.bool,
    syncing: PropTypes.bool,
    fetchedSuccessfully: PropTypes.func,
    fetching: PropTypes.bool
  }),
  pixelCreatesRequestStatus: PropTypes.shape({
    fetched: PropTypes.bool,
    syncing: PropTypes.bool,
    fetchedSuccessfully: PropTypes.func,
    fetching: PropTypes.bool
  }),
  pixelInstallsRequestStatus: PropTypes.shape({
    fetched: PropTypes.bool,
    syncing: PropTypes.bool,
    fetchedSuccessfully: PropTypes.func,
    fetching: PropTypes.bool
  }),
  adAccounts: PropTypes.instanceOf(List),
  preselectedAdAccount: PropTypes.object,
  pixelsByNetwork: PropTypes.object,
  isEventCreation: PropTypes.bool,
  isProOrEnterprise: PropTypes.bool.isRequired,
  hasFacebookCapiFormEventsUpdates: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
  return {
    adAccounts: getAccessibleAdAccounts(state),
    pixelsRequestStatus: state.requestStatus.pixels,
    pixelCreatesRequestStatus: state.requestStatus.pixelCreates,
    pixelInstallsRequestStatus: state.requestStatus.pixelInstalls,
    pixelsByNetwork: getPixelsForInstallation(state),
    isProOrEnterprise: hasAccessToRoiReporting(state),
    hasFacebookCapiFormEventsUpdates: getFacebookCapiFormEventsUpdates(state)
  };
};

export default connect(mapStateToProps)(InstallPixelModalContainer);